<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
import { LoginSignupInputProps } from "~/components/LoginSignup/Organism/LoginInput.props"
import { focusInputById } from "~/utils/input/focus"
import { navigateToLoginSignupPage } from "~/utils/loginSignup/loginSignup"
import { usePasswordValidations } from "~/composables/usePasswordValidations"
import { scrollOptions } from "~/composables/useFocusInvalidInputs"
import { useUserStore } from "~/stores/user"
import { LOGINSIGNUP } from "~/utils/constants"

const { sendEventErrorForm } = useGA4SiteEvents()
const props = withDefaults(defineProps<LoginSignupInputProps>(), {
  showPoliciesDisclaimer: true,
  defaultEmail: "",
  hasDisabledEmail: false,
  hasItemsWithDefaultWidhts: true
})

const emit =
  defineEmits<{
    (e: "registrationSuccess"): void
    (e: "onEdit"): void
  }>()

// Use custom composable for modal that emits events instead of reloading
const useModalLoginRegistration = () => {
  const userStore = useUserStore()

  const { authToken, refreshToken, commerceLayerToken } = {
    authToken: useStatefulCookie("authToken", { maxAge: 86400 }),
    refreshToken: useStatefulCookie("refreshToken", { maxAge: 2592000 }),
    commerceLayerToken: useStatefulCookie("cmlToken", { maxAge: 3600 })
  }
  const errorMessage = ref<string | undefined>(undefined)

  const handleAuth = async (authPromise: Promise<any>) => {
    try {
      errorMessage.value = undefined
      const response = await authPromise
      if (typeof response === "string") {
        if (response === LOGINSIGNUP.resetPassword) {
          errorMessage.value = LOGINSIGNUP.resetPassword
        } else {
          errorMessage.value = response
        }
      } else {
        const {
          authToken: token,
          refreshToken: rToken,
          commerceLayerToken: clToken
        } = response
        authToken.value = token
        refreshToken.value = rToken
        commerceLayerToken.value = clToken
        errorMessage.value = undefined

        // Emit event instead of reloading
        emit("registrationSuccess")
      }
    } catch (error) {
      errorMessage.value = t("loginSignup.signupPage.errorMessage")
      console.warn("ERROR: ", error)
    }
  }

  const handleRegistration = (fields: {
    email: string
    password: string
    subscribeNewsletter: boolean
    privacy?: number
  }) => {
    handleAuth(userStore.signup(fields))
  }

  return {
    errorMessage,
    handleRegistration
  }
}

const { handleRegistration, errorMessage } = useModalLoginRegistration()
const { isEmailInputFocused, isPasswordInputFocused, focusBothInputs } =
  useChromeDoubleFocus()
const { passwordValidations, passwordValidationsMessages } =
  usePasswordValidations()

const emailField = ref(props.defaultEmail || "")
const hasDisabledEmail = ref(props.hasDisabledEmail)
const passwordField = ref("")
const subscribeNewsletter = ref(false)
const privacy = ref(false)
const isLoading = ref(false)
const isPasswordValid = ref(true)
const submitClick = ref(0)

watch(errorMessage, (newValueError) => {
  if (isLoading.value && newValueError !== "") {
    isLoading.value = false
  }
})

const handleSubmit = (fields: {
  email: string
  password: string
  subscribeNewsletter: boolean
  privacy: number
}) => {
  submitClick.value = Math.random() // To trigger a new update every time it gets clicked

  if (privacy.value) {
    isLoading.value = true
    handleRegistration({
      email: fields.email,
      password: fields.password,
      subscribeNewsletter: fields.subscribeNewsletter,
      privacy: Date.now()
    })
  } else {
    const element = document.getElementById("privacy")
      ?.nextElementSibling as HTMLElement
    element?.focus()
    element?.scrollIntoView(scrollOptions)
  }
}

const submitInvalid = () => {
  sendEventErrorForm("registrationForm")
  focusInputById("registration-password")
}

const itemsWidthsClass = [
  props.hasItemsWithDefaultWidhts ? "md:w-[344px] lg:w-[424px]" : "w-full"
]

const handleEditEmail = () => {
  emit("onEdit")
}
</script>

<template>
  <div class="flex w-full flex-col gap-4">
    
<FormKitLazyProvider config-file="true">
<FormKit
      type="form"
      id="registrationForm"
      :actions="false"
      form-class="flex w-full flex-col gap-4 md:items-center"
      :incomplete-message="false"
      @submit-invalid="submitInvalid"
      @submit="handleSubmit"
    >
      <TextfieldsEmail
        name="email"
        :loqate-check="false"
        v-model="emailField"
        :label="$t('loginSignup.emailField')"
        :disabled="hasDisabledEmail"
        :class="itemsWidthsClass"
        :force-focus="focusBothInputs"
        @on-focus-in="isEmailInputFocused = true"
        @on-focus-out="isEmailInputFocused = false"
        @on-edit="handleEditEmail"
      />
      <TextfieldsInputText
        @on-update-validity="isPasswordValid = $event"
        is-required
        v-model="passwordField"
        id="registration-password"
        type="password"
        name="password"
        :validation="passwordValidations"
        :label="
          $t('dialogsAndModals.organism.loginAndRegistration.passwordField')
        "
        :validation-messages="passwordValidationsMessages"
        :class="itemsWidthsClass"
        :force-focus="focusBothInputs"
        @on-focus-in="isPasswordInputFocused = true"
        @on-focus-out="isPasswordInputFocused = false"
      />
      <TextfieldsPasswordCheck
        theme="site"
        v-if="passwordField"
        :password-field="passwordField"
        class="mb-2 w-full"
      />

      <ControlsCheckbox
        v-model="privacy"
        id="privacy"
        name="privacy"
        :class="itemsWidthsClass"
        :label="
          $t('dialogsAndModals.organism.loginAndRegistration.privacyAccept', {
            path: customPaths.privacy
          })
        "
        :forcedErrorMessage="
          submitClick && !privacy
            ? $t('dialogsAndModals.organism.loginAndRegistration.privacyError')
            : ''
        "
      />

      <ControlsCheckbox
        v-model="subscribeNewsletter"
        id="subscribeNewsletter"
        name="subscribeNewsletter"
        :class="itemsWidthsClass"
        :label="
          $t(
            'dialogsAndModals.organism.loginAndRegistration.subscribeNewsletter'
          )
        "
      />
      <UtilsSimpleErrorParagraph
        v-if="errorMessage"
        :message="errorMessage"
      ></UtilsSimpleErrorParagraph>

      <UtilsButton
        type="submit"
        class="h-10 w-full"
        :text="$t('dialogsAndModals.organism.loginAndRegistration.register')"
        :is-loading="isLoading"
      >
        <template #icon>
          <UtilsLoader v-if="isLoading" class="mr-2" />
        </template>
      </UtilsButton>
    </FormKit>
    <TextBlocksAcceptConditions />
</FormKitLazyProvider>

  </div>
</template>
